import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { UserAvatar } from '@/components/UserAvatar';
import VotingButtons from '@/components/VotingButtons';
import { categoryMeta } from '@/lib/categories';
import { IDEA } from '@/constants/testIds';
import { ExternalLink, MapPin, Clock } from 'lucide-react';

export default function IdeaDetailDialog({ open, onOpenChange, idea, onVoteChanged }) {
    if (!idea) return null;
    const cat = categoryMeta(idea.category);
    const Icon = cat.icon;
    const tripId = idea.vote_trip_id || (idea.trip_ids && idea.trip_ids[0]) || null;
    const breakdown = idea.vote_breakdown || { must: 0, nice: 0, skip: 0 };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid={IDEA.detailDialog}
                className="rounded-3xl max-w-2xl p-0 overflow-hidden border-[var(--ns-border)] max-h-[90vh] overflow-y-auto"
            >
                {idea.thumbnail && (
                    <div className="relative h-56 sm:h-72 overflow-hidden">
                        <img src={idea.thumbnail} alt={idea.location_name} className="w-full h-full object-cover" />
                        <span
                            className="absolute top-4 left-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest bg-white/90 backdrop-blur px-3 py-1.5 rounded-full font-medium"
                            style={{ color: cat.color }}
                        >
                            <Icon size={12} /> {cat.label}
                        </span>
                    </div>
                )}
                <div className="p-6 sm:p-8">
                    <DialogHeader className="text-left">
                        <DialogTitle className="font-display text-2xl sm:text-3xl font-semibold tracking-tight leading-tight">
                            {idea.location_name}
                        </DialogTitle>
                    </DialogHeader>
                    {idea.neighborhood && (
                        <div className="flex items-center gap-1.5 text-sm text-[var(--ns-text2)] mt-2">
                            <MapPin size={14} /> {idea.neighborhood}
                        </div>
                    )}
                    {idea.ai_summary && (
                        <p className="text-[var(--ns-text2)] mt-4 leading-relaxed">{idea.ai_summary}</p>
                    )}

                    {idea.note && (
                        <div className="mt-6 p-5 rounded-2xl border" style={{ background: 'var(--ns-soft)', borderColor: 'var(--ns-border)' }}>
                            <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] mb-2">Why we saved this</div>
                            <p className="text-[var(--ns-text)] leading-relaxed">{idea.note}</p>
                        </div>
                    )}

                    <div className="flex items-center justify-between mt-6 flex-wrap gap-3">
                        <div className="flex items-center gap-2">
                            <UserAvatar name={idea.saved_by?.name} picture={idea.saved_by?.picture} size={32} />
                            <div>
                                <div className="text-sm font-medium text-[var(--ns-text)]">{idea.saved_by?.name || 'You'}</div>
                                <div className="text-xs text-[var(--ns-text3)]">saved this idea</div>
                            </div>
                        </div>
                        {idea.best_time && (
                            <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[var(--ns-text3)]">
                                <Clock size={12} /> Best: {idea.best_time}
                            </span>
                        )}
                    </div>

                    {/* Voting */}
                    {tripId && (
                        <div className="mt-7 p-5 rounded-2xl border border-[var(--ns-border)]">
                            <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] mb-3">Your vote</div>
                            <VotingButtons
                                ideaId={idea.inspiration_id}
                                tripId={tripId}
                                initialVote={idea.my_vote}
                                breakdown={breakdown}
                                score={idea.priority_score || 0}
                                onVoted={onVoteChanged}
                                compact={false}
                            />
                        </div>
                    )}

                    {idea.source_url && (
                        <a
                            href={idea.source_url}
                            target="_blank"
                            rel="noreferrer noopener"
                            data-testid={IDEA.openSourceLink}
                            className="inline-flex items-center gap-2 mt-6 rounded-full px-5 h-11 text-sm font-medium border border-[var(--ns-border)] hover:bg-[var(--ns-soft)] transition text-[var(--ns-navy)]"
                        >
                            <ExternalLink size={14} /> Open source
                            {idea.source_domain && <span className="text-[var(--ns-text3)]"> · {idea.source_domain}</span>}
                        </a>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
